import { __decorate, __metadata } from "tslib";
import { elementDefine, mutationObserverLight, onConnectedAfter, onConnectedBodyShadow, queryShadow, resizeObserverLight } from "@dooboostore/simple-web-component";
const tagName = "stock-radar";
var RadarChart_default = (w) => {
  var _a;
  const existing = w.customElements.get(tagName);
  if (existing)
    return existing;
  let RadarChartImpl = class RadarChartImpl extends w.HTMLElement {
    constructor() {
      super(...arguments);
      Object.defineProperty(this, "axes", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "sets", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "canvas", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: void 0
      });
    }
    // 자식 <axis>로 축 정의, <score-set>/<score>로 오버레이 폴리곤 정의
    collect() {
      const axes = [];
      this.querySelectorAll(":scope > axis").forEach((el) => {
        axes.push({
          id: el.getAttribute("id") || el.getAttribute("axis") || `ax${axes.length}`,
          label: el.getAttribute("label") || String(axes.length),
          color: el.getAttribute("color") || "#6366f1"
        });
      });
      this.axes = axes;
      const sets = [];
      this.querySelectorAll(":scope > score-set").forEach((setEl) => {
        const scores = {};
        setEl.querySelectorAll(":scope > score").forEach((sc) => {
          const ax = sc.getAttribute("axis");
          const v = Number(sc.getAttribute("value"));
          if (ax && Number.isFinite(v))
            scores[ax] = v;
        });
        sets.push({
          fill: setEl.getAttribute("fill-style") || void 0,
          stroke: setEl.getAttribute("stroke-style") || void 0,
          strokeWidth: Number(setEl.getAttribute("stroke-width")) || 1,
          scores
        });
      });
      this.sets = sets;
    }
    onConnected() {
      this.collect();
      if (this.canvas)
        this.drawRadar();
    }
    // 자식 변경 시 재수집 후 다시 그림
    onMutated() {
      this.collect();
      if (this.canvas)
        this.drawRadar();
    }
    render() {
      return `
        <style>
          :host { display: block; }
          #radar-canvas { display: block; width: 100%; height: 100%; }
        </style>
        <canvas id="radar-canvas"></canvas>
      `;
    }
    onHostResize() {
      if (this.isConnected)
        this.drawRadar();
    }
    drawRadar() {
      const canvas = this.canvas;
      const axes = this.axes;
      if (!canvas || axes.length === 0)
        return;
      const n = axes.length;
      const dpr = w.devicePixelRatio || 1;
      const W = canvas.clientWidth || 360;
      const H = canvas.clientHeight || 360;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      const ctx = canvas.getContext("2d");
      if (!ctx)
        return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      const cx = W / 2, cy = H / 2, R = Math.min(W, H) * 0.36;
      const angle = (i) => -Math.PI / 2 + Math.PI * 2 * i / n;
      const pt = (i, v) => [cx + Math.cos(angle(i)) * R * (v / 100), cy + Math.sin(angle(i)) * R * (v / 100)];
      const GRID_STEPS = 5;
      const GRID_LABELS = ["20%", "40%", "60%", "80%", "100%"];
      for (let r = 1; r <= GRID_STEPS; r++) {
        const pct = r / GRID_STEPS * 100;
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
          const [x, y] = pt(i, pct);
          if (i === 0)
            ctx.moveTo(x, y);
          else
            ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = r === GRID_STEPS ? "#c8cdd6" : "#eef0f4";
        ctx.lineWidth = r === GRID_STEPS ? 1.5 : 1;
        ctx.stroke();
        const [lx, ly] = pt(0, pct);
        ctx.font = "9px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = "#b0b8c6";
        ctx.fillText(GRID_LABELS[r - 1], lx, ly - 2);
      }
      ctx.strokeStyle = "#eef0f4";
      ctx.lineWidth = 1;
      for (let i = 0; i < n; i++) {
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        const [x, y] = pt(i, 100);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      for (const set of this.sets) {
        ctx.beginPath();
        axes.forEach((a, i) => {
          const v = set.scores[a.id] ?? 50;
          const [x, y] = pt(i, v);
          if (i === 0)
            ctx.moveTo(x, y);
          else
            ctx.lineTo(x, y);
        });
        ctx.closePath();
        if (set.fill) {
          ctx.fillStyle = set.fill;
          ctx.fill();
        }
        if (set.stroke) {
          ctx.strokeStyle = set.stroke;
          ctx.lineWidth = set.strokeWidth;
          ctx.stroke();
        }
      }
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const lineHeight = 13;
      axes.forEach((a, i) => {
        const lines = a.label.split("\n");
        const [x, y] = pt(i, 120);
        const totalH = lines.length * lineHeight;
        ctx.font = "bold 10px sans-serif";
        ctx.fillStyle = a.color;
        lines.forEach((line, li) => {
          ctx.fillText(line, x, y - totalH / 2 + li * lineHeight + lineHeight / 2);
        });
      });
    }
  };
  __decorate([
    onConnectedAfter,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], RadarChartImpl.prototype, "onConnected", null);
  __decorate([
    mutationObserverLight({ childList: true, attributes: true, subtree: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], RadarChartImpl.prototype, "onMutated", null);
  __decorate([
    onConnectedBodyShadow,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
  ], RadarChartImpl.prototype, "render", null);
  __decorate([
    resizeObserverLight,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], RadarChartImpl.prototype, "onHostResize", null);
  __decorate([
    queryShadow("#radar-canvas"),
    __metadata("design:type", typeof (_a = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _a : Object)
  ], RadarChartImpl.prototype, "canvas", void 0);
  RadarChartImpl = __decorate([
    elementDefine(tagName, { window: w })
  ], RadarChartImpl);
  return RadarChartImpl;
};
export {
  RadarChart_default as default
};
//# sourceMappingURL=RadarChart.js.map
